import * as firebase from 'firebase/app'
import 'firebase/database'
import chat from './chat'
import video from './video'
import * as DB from '../../database'
import { event } from 'vue-analytics'
import * as Constants from '../../constants'

export default {
  namespaced: true,
  modules: {
    chat: chat,
    video: video
  },
  state: {
    localId: null,
    remoteId: null,
    roomId: null,
    roomEmptyHandlerRef: null,
    onRoomTerminatedByPeer: null,
    startTime: new Date()
  },
  mutations: {
    setLocalId (state, { localId }) {
      global.DEBUG && console.log('setLocalId: ' + localId)
      state.localId = localId
    },
    setRemoteId (state, { remoteId }) {
      global.DEBUG && console.log('setRemoteId: ' + remoteId)
      state.remoteId = remoteId
    },
    setRoomId (state, { roomId }) {
      global.DEBUG && console.log('setRoomId: ' + roomId)
      state.roomId = roomId
    },
    setRoomEmptyHandlerRef (state, { roomEmptyHandlerRef }) {
      global.DEBUG && console.log('setRoomEmptyHandlerRef: ' + roomEmptyHandlerRef)
      state.roomEmptyHandlerRef = roomEmptyHandlerRef
    },
    setOnRoomTerminatedByPeer (state, { onRoomTerminatedByPeer }) {
      state.onRoomTerminatedByPeer = onRoomTerminatedByPeer
    },
    setStartTime (state, { startTime }) {
      global.DEBUG && console.log('setStartTime: ' + startTime)
      state.startTime = startTime
    }
  },
  actions: {
    async create ({ dispatch, commit, state }, { localId, remoteId, onRoomTerminatedByPeer }) {
      commit('setLocalId', { localId: localId })

      // Leave the current room if needed
      if (state.roomId !== null) { await dispatch('leave', { isInitiator: true }) }

      // Create a new room
      let roomId = firebase.database().ref(DB.ROOMS).push().key
      global.DEBUG && console.log(`creating room ${roomId}`)
      let tmpRef = firebase.database().ref(String.format(DB.ROOM_USER, roomId, localId))
      await Promise.all([
        tmpRef.set(firebase.database.ServerValue.TIMESTAMP),
        dispatch('chat/start', { localId: localId, remoteId: remoteId, roomId: roomId }),
        dispatch('video/start', { localId: localId, remoteId: remoteId, roomId: roomId, sendOffer: false })
      ])
      tmpRef.onDisconnect().remove()
      commit('setRoomId', { roomId: roomId })
      commit('setRemoteId', { remoteId: remoteId })

      // Listen for empty room
      commit('setOnRoomTerminatedByPeer', { onRoomTerminatedByPeer: onRoomTerminatedByPeer })
      let roomEmptyHandlerRef = firebase.database().ref(String.format(DB.ROOM_USERS, roomId)).on('child_removed', () => {
        dispatch('leave', { isInitiator: false })
        if (onRoomTerminatedByPeer) {
          onRoomTerminatedByPeer()
        }
      })
      commit('setRoomEmptyHandlerRef', { roomEmptyHandlerRef: roomEmptyHandlerRef })

      // Invite the other user to join the room
      await dispatch('invite', { remoteId: remoteId })
      commit('setStartTime', { startTime: new Date() })
    },

    async invite ({ state }, { remoteId }) {
      global.DEBUG && console.log(`inviting ${remoteId} to join the room`)
      let invitationId = firebase.database().ref(String.format(DB.INVITATIONS, remoteId)).push().key
      await firebase.database().ref(String.format(DB.INVITATION, remoteId, invitationId)).set({ senderId: state.localId, roomId: state.roomId })
    },

    async join ({ dispatch, commit, state }, { localId, invitation, onRoomTerminatedByPeer }) {
      global.DEBUG && console.log(`joining room ${invitation.roomId}`)
      commit('setLocalId', { localId: localId })

      // Leave the current room if needed
      if (state.roomId !== null) { await dispatch('leave', { isInitiator: true }) }

      // Listen for empty room
      commit('setOnRoomTerminatedByPeer', { onRoomTerminatedByPeer: onRoomTerminatedByPeer })
      let roomEmptyHandlerRef = firebase.database().ref(String.format(DB.ROOM_USERS, invitation.roomId)).on('child_removed', () => {
        dispatch('leave', { isInitiator: false })
        if (onRoomTerminatedByPeer) {
          onRoomTerminatedByPeer()
        }
      })
      commit('setRoomEmptyHandlerRef', { roomEmptyHandlerRef: roomEmptyHandlerRef })

      // Join the room
      let tmpRef = firebase.database().ref(String.format(DB.ROOM_USER, invitation.roomId, localId))
      await tmpRef.set(firebase.database.ServerValue.TIMESTAMP)
      tmpRef.onDisconnect().remove()
      commit('setRoomId', { roomId: invitation.roomId })
      commit('setRemoteId', { remoteId: invitation.senderId })

      // Start the chat and the video
      await Promise.all([
        dispatch('chat/start', { localId: localId, remoteId: state.remoteId, roomId: invitation.roomId }),
        dispatch('video/start', { localId: localId, remoteId: state.remoteId, roomId: invitation.roomId, sendOffer: true })
      ])
      commit('setStartTime', { startTime: new Date() })
    },

    async leave ({ state, commit, dispatch }, { isInitiator }) {
      if (state.roomId === null) {
        return
      }

      global.DEBUG && console.log(`leaving room ${state.roomId}`)

      // Report to Google Analytics
      let duration = Math.round((new Date() - state.startTime) / 1000)
      event({
        eventCategory: 'Room',
        eventAction: 'Exit',
        eventValue: isInitiator ? 1 : 0,
        [Constants.CUSTOM_METRIC_ID_ROOM_DURATION]: duration,
        [Constants.CUSTOM_METRIC_ID_CHAT_MESSAGE_COUNT]: state.chat.messages.length,
        [Constants.CUSTOM_METRIC_ID_VIDEO_CONNEXION_SUCCESSFUL]: state.video.connectionSuccessful ? 1 : 0,
        [Constants.CUSTOM_METRIC_ID_VIDEO_CONNEXION_RELAYED]: state.video.connectionRelayed ? 1 : 0
      })

      // Stop listening for an empty room
      firebase.database().ref(String.format(DB.ROOM_USERS, state.roomId)).off('child_removed', state.roomEmptyHandlerRef)
      commit('setRoomEmptyHandlerRef', { roomEmptyHandlerRef: null })

      // Leave the room
      await Promise.all([
        firebase.database().ref(String.format(DB.ROOM_USER, state.roomId, state.localId)).remove(),
        dispatch('chat/stop'),
        dispatch('video/stop')
      ])
      commit('setRoomId', { roomId: null })
      commit('setRemoteId', { remoteId: null })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/room/index.js